import { useEffect, useState } from 'react';
import { Img } from '../../display/Img';
import styles from './Avatar.module.scss';

export type Props = {
  /**
   * Controls whether `Avatar` is hidden from screen readers.
   * Useful when it renders alongside redundant content.
   */
  'aria-hidden'?: boolean;

  /**
   * Background color of the Avatar when no image exists.
   * When omitted, will default to `primary`, `secondary`, or `tertiary` based on the child number it is.
   */
  color?: 'primary' | 'secondary' | 'tertiary';

  /**
   * Explicit initials to display when no image is available.
   * When omitted, initials are derived from `name`.
   */
  initials?: string;

  /** Indicates whether to render the component as a block-level element. */
  isBlock?: boolean;

  /** Indicates whether the Avatar is interactive, i.e. that it lives within an interactive node. */
  isInteractive?: boolean;

  /** Indicates whether the Avatar is selected / clicked on. */
  isSelected?: boolean;

  /** Name of user */
  name: string;

  /** Whether or not to lazy load the underlying image. */
  shouldLazilyLoad?: boolean;

  /** Size of the `Avatar` to display.
   * @default 'small'
   */
  size?: 'small' | 'medium' | 'large';

  /** URL to image */
  url?: string | null;
};

const getNameInitials = (name: string): string => {
  const initials = (name || '')
    .trim()
    .split(/\s+/)
    .map((s) => s[0]);
  const firstLetter = initials[0];
  const lastLetter = initials[initials.length - 1];

  if (!firstLetter) {
    return '';
  }

  return initials.length === 1
    ? String(firstLetter)
    : `${firstLetter}${lastLetter}`;
};

const SIZES = {
  small: 24,
  medium: 36,
  large: 48,
};

/**
 * Displays a user's avatar. Falls back to their initials if an invalid
 * image url or no image url is provided.
 */
export const Avatar = ({
  'aria-hidden': ariaHidden,
  color,
  initials: initialsProp,
  isBlock,
  isInteractive,
  isSelected,
  name,
  url,
  shouldLazilyLoad = true,
  size = 'small',
}: Props) => {
  // Used to fall back to initials if the image fails to load
  const [hasError, setHasError] = useState(false);

  let initials: string | null = null;
  if (initialsProp) {
    initials = initialsProp;
  } else if (name) {
    initials = getNameInitials(name);
  }

  // reset hasError if the url changes
  useEffect(() => {
    if (hasError) {
      setHasError(false);
    }
    // we don't want hasError to trigger this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const showInitials = !url || (url && hasError);

  return (
    <div
      aria-hidden={ariaHidden}
      aria-label={name}
      className={styles.avatar}
      data-block={isBlock ? 'true' : undefined}
      data-color={color}
      data-show-initials={showInitials}
      data-interactive={isInteractive ? 'true' : undefined}
      data-selected={isSelected ? 'true' : undefined}
      data-size={size}
      data-testid="Avatar"
      data-zds
      role="img"
    >
      {url && !hasError ? (
        <Img
          className={styles.img}
          alt={name}
          aria-hidden={true}
          height={SIZES[size]}
          onError={() => setHasError(true)}
          src={url}
          width={SIZES[size]}
          loading={shouldLazilyLoad ? 'lazy' : 'eager'}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
};
